import type {
  CustomChartTableSettings,
  DashboardPreset,
  DashboardPresetFilters,
} from '@/entities/dashboards/model/api/types';
import { DIALOG_EVENT_FILTERS } from '@/entities/conversations';
import {
  COPILOT_VIEWS,
  DIALOGS_LAYOUTS,
  SIDEBAR_PANELS,
  TABLE_SIDE_PANELS,
  DashboardPresetsSettings,
  DialogsSettings,
  SidebarSettings,
  TableSettings,
  UiSettings,
} from '../types';
import { isServerDashboardPresetId } from '../dashboardPresetKeys';

/** Проверка значения одного поля сохранённого набора на соответствие текущему составу настроек. */
type FieldGuard = (value: unknown) => boolean;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isString: FieldGuard = (value) => typeof value === 'string';

const isStringArray: FieldGuard = (value) => Array.isArray(value) && value.every(isString);

const isObjectArray: FieldGuard = (value) => Array.isArray(value) && value.every(isPlainObject);

const isFiniteNumber: FieldGuard = (value) => typeof value === 'number' && Number.isFinite(value);

const isBoolean: FieldGuard = (value) => typeof value === 'boolean';

const isOneOf =
  (allowed: readonly string[]): FieldGuard =>
  (value) =>
    typeof value === 'string' && allowed.includes(value);

const isArrayOf =
  (allowed: readonly string[]): FieldGuard =>
  (value) =>
    Array.isArray(value) && value.every(isOneOf(allowed));

/**
 * Оставляет в секции только те поля, значения которых система понимает. Поле, не прошедшее
 * проверку, отбрасывается — вместо него применится значение по умолчанию. Поля, для которых
 * правила нет, остаются нетронутыми: их мог записать другой релиз приложения, и терять их
 * при обратном сохранении набора нельзя. Секция не объект — секция берётся по умолчанию.
 */
const dropInvalidFields = <T>(section: unknown, guards: Record<string, FieldGuard>): T | undefined => {
  if (!isPlainObject(section)) return undefined;
  const result = { ...section };
  for (const [key, isValid] of Object.entries(guards)) {
    if (key in result && !isValid(result[key])) delete result[key];
  }
  return result as T;
};

const DIALOGS_PAGE_GUARDS: Record<string, FieldGuard> = {
  layout: isOneOf(DIALOGS_LAYOUTS),
  attributes: isStringArray,
  mainColumns: isStringArray,
  expertReport: isObjectArray,
  hiddenDataTabs: isStringArray,
  lastDataTab: isString,
  expertReportRuleResults: isStringArray,
  transcriptEventFilters: isArrayOf(DIALOG_EVENT_FILTERS),
  attrCollapsedNodes: isStringArray,
  attrTopLevelOrder: isStringArray,
  attrNavCollapsed: isBoolean,
  attrNavWidth: isFiniteNumber,
  textAttributesPanelOpen: isBoolean,
};

const SIDEBAR_GUARDS: Record<string, FieldGuard> = {
  activePanel: isOneOf(SIDEBAR_PANELS),
  copilotView: isOneOf(COPILOT_VIEWS),
  copilotActiveChatId: (value) => value === null || isFiniteNumber(value),
  copilotWidth: isFiniteNumber,
  isOpen: isBoolean,
};

const TABLE_GUARDS: Record<string, FieldGuard> = {
  filters: isObjectArray,
  sorting: isObjectArray,
  offset: isFiniteNumber,
  search: isPlainObject,
  contentSearch: (value) => value === null || isPlainObject(value),
  columnOrder: isStringArray,
  columnVisibility: isPlainObject,
  columnPinning: isPlainObject,
  columnsPanelWidth: isFiniteNumber,
  columnsPanelExpandedGroups: isStringArray,
  activeSidePanel: isOneOf(TABLE_SIDE_PANELS),
  sidePanelPinned: (value) => typeof value === 'boolean',
  scrollIndex: isFiniteNumber,
  scrollKey: isString,
  dismissedSelectionKey: isString,
  selectionDescription: isPlainObject,
  name: isString,
  selectionSource: isString,
  activePresetId: isString,
  activePresetView: isPlainObject,
  pinnedToMenu: (value) => typeof value === 'boolean',
  previousView: isPlainObject,
  previousPresetId: isString,
};

/** Записи таблиц, значение которых не является набором настроек, к применению не берутся. */
const normalizeTables = (tables: unknown): Record<string, TableSettings> | undefined => {
  if (!isPlainObject(tables)) return undefined;
  const result: Record<string, TableSettings> = {};
  for (const [name, value] of Object.entries(tables)) {
    const table = dropInvalidFields<TableSettings>(value, TABLE_GUARDS);
    if (table) result[name] = table;
  }
  return result;
};

/**
 * Дашборд-пресет к применению не берётся, если из него нельзя показать дашборд: нет названия
 * или состава диаграмм. Сами диаграммы не разбираются — их состав задаёт контракт наполнения,
 * и непонятная диаграмма отказывает в своём блоке, не роняя остальной пресет.
 */
const isDashboardPreset = (value: unknown): value is DashboardPreset =>
  isPlainObject(value) && typeof value.name === 'string' && Array.isArray(value.charts);

/**
 * Фильтры панели дашборда, которые можно показать в панели. Сами атрибутные фильтры не разбираются:
 * непонятный атрибут панель покажет без своего поля, не теряя остальных фильтров. Консультант,
 * сохранённый в фильтрах раньше, проверку не валит и не читается.
 */
export const isDashboardPresetFilters = (value: unknown): value is DashboardPresetFilters =>
  isPlainObject(value) && isString(value.dateFrom) && isString(value.dateTo) && isObjectArray(value.attributeFilters);

/** Непонятные фильтры у пресета отбрасываются — он показывает фильтры по умолчанию, а не пропадает. */
const normalizeDashboardPreset = (preset: DashboardPreset): DashboardPreset => {
  if (preset.filters === undefined || isDashboardPresetFilters(preset.filters)) return preset;
  const { filters: _dropped, ...rest } = preset;
  return rest;
};

/** Записи пресетов, значение которых не является дашбордом-пресетом, к применению не берутся. */
const normalizeDashboardPresets = (section: unknown): DashboardPresetsSettings | undefined => {
  if (!isPlainObject(section)) return undefined;
  const presets: Record<string, DashboardPreset> = {};
  if (isPlainObject(section.presets)) {
    for (const [id, value] of Object.entries(section.presets)) {
      if (isDashboardPreset(value)) presets[id] = normalizeDashboardPreset(value);
    }
  }
  const storedActiveId = section.activePresetId;
  // Активным может быть только уцелевший пресет: ссылка в никуда оставила бы страницу
  // без дашборда вовсе — ни пользовательского, ни стандартного. Системный и совместный дашборды
  // в настройках не хранятся, и уцелел ли такой, известно только по списку с сервера — там его и сверяют.
  const activePresetId =
    typeof storedActiveId === 'string' && (presets[storedActiveId] || isServerDashboardPresetId(storedActiveId))
      ? storedActiveId
      : undefined;
  const chartTables: Record<string, CustomChartTableSettings> = {};
  if (isPlainObject(section.chartTables)) {
    for (const [chartId, value] of Object.entries(section.chartTables)) {
      // Вид таблицы держится за колонками, которых в выборке может уже не быть: непонятную
      // запись отбрасываем целиком, а знакомую отдаём как есть — состав колонок сверяет сама
      // таблица, когда получит выборку.
      if (isPlainObject(value) && Array.isArray(value.visibleColumns) && Array.isArray(value.columnOrder)) {
        chartTables[chartId] = value as unknown as CustomChartTableSettings;
      }
    }
  }
  const sharedFilters: Record<string, DashboardPresetFilters> = {};
  if (isPlainObject(section.sharedFilters)) {
    for (const [presetId, value] of Object.entries(section.sharedFilters)) {
      if (isServerDashboardPresetId(presetId) && isDashboardPresetFilters(value)) {
        sharedFilters[presetId] = value;
      }
    }
  }
  return { presets, activePresetId, chartTables, sharedFilters };
};

/**
 * Приводит набор настроек из профиля к текущему составу настроек приложения.
 *
 * Набор копится у пользователя годами и мог быть записан релизом с другим составом настроек:
 * значение, которого система уже не понимает (устаревший формат, отсутствующая возможность),
 * отбрасывается и применяется по умолчанию, а остальные сохранённые настройки применяются как
 * сохранены. Без этого один непонятный элемент набора роняет экран у давнего пользователя.
 */
export const normalizeUiSettings = (settings: UiSettings): UiSettings => ({
  ...settings,
  // Код темы задаётся набором тем проекта и заранее не известен: проверяем только то,
  // на чём сломается применение темы
  theme: isString(settings.theme) && settings.theme ? settings.theme : undefined,
  dialogsPage: dropInvalidFields<Partial<DialogsSettings>>(settings.dialogsPage, DIALOGS_PAGE_GUARDS),
  sidebar: dropInvalidFields<SidebarSettings>(settings.sidebar, SIDEBAR_GUARDS),
  tables: normalizeTables(settings.tables),
  dashboardQuality: isPlainObject(settings.dashboardQuality) ? settings.dashboardQuality : undefined,
  dashboardPresets: normalizeDashboardPresets(settings.dashboardPresets),
});
